import { useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Column, DataTable } from "components/tables/DataTable"

import { gameService } from "services/game/game.service"
import { CategoryRecord, CategorySeoEntry } from "services/game/type"
import { SEO_LOCALES } from "services/seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { CategorySeoModal } from "../CategorySeoModal"
import { AsyncList, IndexFlag, InfoBox, inputClass, LocaleChips, PathText, primaryButtonClass } from "../CmsUi"

/** Categories are pages at /casino/<slug>. Game Management still owns everything else. */
export function CategorySeoTab() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [categories, setCategories] = useState<CategoryRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [editing, setEditing] = useState<CategoryRecord | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setCategories((await gameService.listCategories()) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const localesWithSeo = (category: CategoryRecord): string[] =>
    SEO_LOCALES.filter(code => {
      const entry = category.seo?.[code] as CategorySeoEntry | undefined
      return Boolean(entry?.title || entry?.description || entry?.h1)
    })

  const needle = search.trim().toLowerCase()
  const visible = categories.filter(
    category =>
      !needle ||
      category.slug.toLowerCase().includes(needle) ||
      category.key.toLowerCase().includes(needle) ||
      (category.title?.en ?? "").toLowerCase().includes(needle)
  )

  const columns: Column<CategoryRecord>[] = [
    {
      header: t("cmsSeo.col.path", { defaultValue: "Path" }),
      render: category =>
        site.origin ? (
          <a
            href={site.url(site.defaultLocale, `/casino/${category.slug}`)}
            target="_blank"
            rel="noreferrer"
            onClick={event => event.stopPropagation()}
          >
            <PathText className="underline decoration-dotted underline-offset-2">/casino/{category.slug}</PathText>
          </a>
        ) : (
          <PathText>/casino/{category.slug}</PathText>
        )
    },
    {
      header: t("cmsSeo.col.title", { defaultValue: "Title" }),
      wrap: true,
      render: category => (
        <span className="text-sm">
          {(category.seo?.en as CategorySeoEntry | undefined)?.title || (
            <span className="opacity-50">{category.title?.en ?? category.key}</span>
          )}
        </span>
      )
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "md",
      render: category => <IndexFlag indexed={category.seo?.noindex !== true} />
    },
    {
      header: t("cmsSeo.col.locales", { defaultValue: "Locales" }),
      hideBelow: "lg",
      render: category => <LocaleChips locales={localesWithSeo(category)} missing={[]} />
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: category => (
        <button
          type="button"
          className={primaryButtonClass}
          onClick={event => {
            event.stopPropagation()
            setEditing(category)
          }}
        >
          {t("common.edit", { defaultValue: "Edit" })}
        </button>
      )
    }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <label className="flex max-w-md flex-col gap-1.5">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {t("common.search", { defaultValue: "Search" })}
        </span>
        <input
          className={inputClass}
          value={search}
          onChange={event => setSearch(event.target.value)}
          placeholder={t("cmsSeo.searchCategories", { defaultValue: "Search by slug or name…" }) as string}
        />
      </label>

      <InfoBox>
        {t("cmsSeo.categoriesInfo", {
          defaultValue:
            "Game categories are pages at /casino/<slug>, so their URL and search metadata live here rather than in Game Management. Categories themselves are still created, ordered and filled with games there."
        })}
        <div className="mt-2">
          {t("cmsSeo.categoriesSlugInfo", {
            defaultValue:
              "Renaming a slug redirects the old URL automatically, and the metadata follows the category rather than the path — so a rename never loses it."
          })}
        </div>
      </InfoBox>

      <AsyncList
        loading={loading}
        isEmpty={visible.length === 0}
        emptyText={t("cmsSeo.noCategories", { defaultValue: "No categories yet — create them in Game Management." }) as string}
      >
        <DataTable rows={visible} rowKey={category => category.key} columns={columns} onRowClick={setEditing} />
      </AsyncList>

      <CategorySeoModal category={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  )
}
